'use client';

import type { IdentityProvidersNames } from '@repo/api-gw-sdk';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags, useLaunchDarkly } from '@/contexts/useFeatureFlags';

import { LoginPage } from './loginPage';

import { LoginV2DomainPage } from '../login-v2/loginV2DomainPage';
import { LoginV2Page } from '../login-v2/loginV2Page';

interface LoginSwitchProps {
  isSuffixPublicDomain: boolean;
  isExactPublicDomain: boolean;
  hasAuthToken: boolean;
  hasError: boolean;
  idpData: IdentityProvidersNames;
}

export function LoginSwitch(props: LoginSwitchProps) {
  const { LoginV2Enabled } = useFeatureFlags();
  const { isReady } = useLaunchDarkly();

  if (!isReady) {
    return <Loader />;
  }

  const loginV2Enabled = LoginV2Enabled === true;

  return loginV2Enabled ? (
    <LoginV2Branch
      isExactPublicDomain={props.isExactPublicDomain}
      hasAuthToken={props.hasAuthToken}
      idpData={props.idpData}
    />
  ) : (
    <LoginV1Branch
      isSuffixPublicDomain={props.isSuffixPublicDomain}
      hasError={props.hasError}
    />
  );
}

function LoginV1Branch({
  isSuffixPublicDomain,
  hasError,
}: Pick<LoginSwitchProps, 'isSuffixPublicDomain' | 'hasError'>) {
  const { clientApiBaseUrl, isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const shouldRedirect = isSuffixPublicDomain && !hasError;

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    let url = `${clientApiBaseUrl}/login`;
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl && !isDev) {
      url += `?returnUrl=${returnUrl}`;
    }
    window.location.replace(url);
  }, [shouldRedirect, clientApiBaseUrl, isDev, searchParams]);

  if (shouldRedirect) {
    return <Loader />;
  }
  return <LoginPage />;
}

function LoginV2Branch({
  isExactPublicDomain,
  hasAuthToken,
  idpData,
}: Pick<LoginSwitchProps, 'isExactPublicDomain' | 'hasAuthToken' | 'idpData'>) {
  const { isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasDomain = Boolean(searchParams.get('domain'));
  const redirectHome = !isExactPublicDomain && !isDev && hasAuthToken;

  useEffect(() => {
    if (redirectHome) {
      router.replace('/home');
    }
  }, [redirectHome, router]);

  if (isExactPublicDomain) {
    return <LoginV2DomainPage />;
  }
  if (isDev) {
    return hasDomain ? (
      <LoginV2Page idpData={idpData} />
    ) : (
      <LoginV2DomainPage />
    );
  }
  if (redirectHome) {
    return <Loader />;
  }
  return <LoginV2Page idpData={idpData} />;
}
